import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, RefreshCw, Trash2, Upload } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError, apiFetch } from "@/lib/api";
import {
  type AssetRow,
  type AttachmentRow,
  type LicenseRow,
  listResource,
  type RequestRow,
} from "@/lib/master-data";
import { formatDateTime } from "@/lib/utils";

const TARGET_TYPES = [
  { value: "asset", label: "资产" },
  { value: "request", label: "申请" },
  { value: "license", label: "软件许可" },
  { value: "maintenance", label: "维修" },
  { value: "label", label: "标签任务" },
  { value: "other", label: "其他" },
];

function sizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function AttachmentsPage() {
  const queryClient = useQueryClient();
  const [targetType, setTargetType] = useState("asset");
  const [targetId, setTargetId] = useState("");
  const [category, setCategory] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const listQuery = useQuery({
    queryKey: ["attachments"],
    queryFn: () => listResource<AttachmentRow>("/api/v1/attachments"),
  });
  const assetsQuery = useQuery({
    queryKey: ["assets"],
    queryFn: () => listResource<AssetRow>("/api/v1/assets"),
  });
  const requestsQuery = useQuery({
    queryKey: ["requests", "mine"],
    queryFn: () => listResource<RequestRow>("/api/v1/requests?scope=mine"),
  });
  const licensesQuery = useQuery({
    queryKey: ["licenses"],
    queryFn: () => listResource<LicenseRow>("/api/v1/licenses"),
  });

  const options =
    targetType === "asset"
      ? (assetsQuery.data ?? []).map((row) => ({
          id: row.id,
          label: `${row.assetCode} · ${row.serialNumber ?? ""}`,
        }))
      : targetType === "request"
        ? (requestsQuery.data ?? []).map((row) => ({
            id: row.id,
            label: `${row.requestNo} · ${row.material?.name ?? ""}`,
          }))
        : targetType === "license"
          ? (licensesQuery.data ?? []).map((row) => ({ id: row.id, label: row.licenseCode }))
          : [];

  async function upload() {
    if (!targetId.trim()) return toast.error("请选择或填写关联对象");
    if (!file) return toast.error("请选择文件");
    setBusy(true);
    try {
      const form = new FormData();
      form.set("targetType", targetType);
      form.set("targetId", targetId);
      if (category.trim()) form.set("category", category.trim());
      form.set("file", file);
      await apiFetch<AttachmentRow>("/api/v1/attachments", { method: "POST", body: form });
      toast.success("附件已上传");
      setFile(null);
      await queryClient.invalidateQueries({ queryKey: ["attachments"] });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "上传失败");
    } finally {
      setBusy(false);
    }
  }

  async function download(row: AttachmentRow) {
    try {
      const response = await fetch(`/api/v1/attachments/${row.id}/download`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("下载失败");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = row.fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "下载失败");
    }
  }

  async function remove(row: AttachmentRow) {
    if (!window.confirm(`确认删除附件“${row.fileName}”？R2 文件也会删除。`)) return;
    try {
      await apiFetch(`/api/v1/attachments/${row.id}`, { method: "DELETE" });
      toast.success("附件已删除");
      await queryClient.invalidateQueries({ queryKey: ["attachments"] });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "删除失败");
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="附件管理"
        description="附件存储于 Cloudflare R2；单文件 ≤ 5MB，服务端计算 SHA-256，下载需登录并写入审计。"
        actions={
          <Button variant="outline" onClick={() => listQuery.refetch()}>
            <RefreshCw className="h-4 w-4" />
            刷新
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>上传附件</CardTitle>
          <CardDescription>
            支持 PDF、图片、Office、CSV、TXT、ZIP；文件类型与大小在服务端强制校验。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-5">
            <div className="space-y-2">
              <Label>关联类型</Label>
              <select
                value={targetType}
                onChange={(event) => {
                  setTargetType(event.target.value);
                  setTargetId("");
                }}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                {TARGET_TYPES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>关联对象</Label>
              {options.length > 0 ? (
                <select
                  value={targetId}
                  onChange={(event) => setTargetId(event.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                >
                  <option value="">请选择</option>
                  {options.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  value={targetId}
                  onChange={(event) => setTargetId(event.target.value)}
                  placeholder="对象 ID"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>分类（选填）</Label>
              <Input
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                placeholder="合同 / 验收单"
              />
            </div>
            <div className="space-y-2">
              <Label>文件</Label>
              <input
                type="file"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                className="block w-full text-xs"
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={upload} disabled={busy}>
              <Upload className="h-4 w-4" />
              {busy ? "上传中…" : "上传"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>附件列表</CardTitle>
          <CardDescription>R2 对象键不暴露给前端；下载通过鉴权接口流式返回。</CardDescription>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="space-y-2">
              {["f1", "f2", "f3"].map((key) => (
                <Skeleton key={key} className="h-11 w-full" />
              ))}
            </div>
          ) : (listQuery.data ?? []).length === 0 ? (
            <EmptyState title="暂无附件" description="上传合同、验收单、维修凭证等文件。" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>文件</TableHead>
                  <TableHead>关联</TableHead>
                  <TableHead>大小</TableHead>
                  <TableHead>SHA-256</TableHead>
                  <TableHead>上传时间</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(listQuery.data ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">
                      {row.fileName}
                      <span className="ml-2 text-xs text-muted-foreground">{row.contentType}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{row.targetType}</Badge>
                      <span className="ml-2 font-mono text-xs">{row.targetId.slice(0, 8)}…</span>
                    </TableCell>
                    <TableCell>{sizeLabel(row.sizeBytes)}</TableCell>
                    <TableCell className="font-mono text-xs">{row.sha256.slice(0, 12)}…</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDateTime(row.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => download(row)}>
                          <Download className="h-3.5 w-3.5" />
                          下载
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(row)}>
                          <Trash2 className="h-3.5 w-3.5" />
                          删除
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
